import React from 'react';
import LegalPage from './LegalPage';

export default function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="28 February 2026">
      <section>
        <h2>Overview</h2>
        <p>
          Rental P.A. ("we", "us", "our") is a property-management tool built for
          UK landlords. This policy explains what personal data we collect, how
          we use it, and the choices you have. We aim to keep this short and
          plain-English. If anything is unclear, email us at the address at the
          bottom of this page.
        </p>
      </section>

      <section>
        <h2>What we collect</h2>
        <ul>
          <li>
            <strong>Account data</strong> — your email address, name, and a
            password hash. We never store your password in plain text.
          </li>
          <li>
            <strong>Property data you enter</strong> — property addresses,
            tenant names, tenant email/phone (where you've added them), rent
            amounts, tenancy dates, and any notes.
          </li>
          <li>
            <strong>Documents you upload</strong> — Gas Safety, EPC, EICR,
            tenancy agreements, insurance schedules, and similar. These are
            stored on Cloudinary, our file-storage provider.
          </li>
          <li>
            <strong>Usage data</strong> — basic logs (timestamps, IP addresses,
            errors) so we can keep the service running and debug issues.
          </li>
        </ul>
      </section>

      <section>
        <h2>How we use it</h2>
        <ul>
          <li>To run your account and deliver the features you signed up for.</li>
          <li>To send you renewal reminders and the optional weekly digest email.</li>
          <li>
            To run AI extraction on documents you upload (so we can read the
            expiry date for you). We send the document contents to Google's
            Gemini model via the emergentintegrations library. Google does not
            use this data to train their models when accessed through this API.
          </li>
          <li>
            To send tenant renewal emails on your behalf via Resend, when you
            click the "Email tenant" button. Your tenant's email is the only
            recipient; your own email is set as the reply-to address.
          </li>
        </ul>
      </section>

      <section>
        <h2>Email sending</h2>
        <p>
          We use <strong>Resend</strong> as our transactional email provider.
          Email content (subject + body) you write for tenant renewals is sent
          through Resend's servers and stored briefly by them in line with their
          own privacy policy. We don't share your tenant's email with any third
          party other than the email delivery infrastructure required to reach
          their inbox.
        </p>
      </section>

      <section>
        <h2>Who we share data with</h2>
        <p>We share data only with the service providers needed to run the app:</p>
        <ul>
          <li><strong>MongoDB</strong> — database hosting (your account + property data).</li>
          <li><strong>Cloudinary</strong> — document file storage.</li>
          <li><strong>Google (Gemini)</strong> — AI-assisted document extraction.</li>
          <li><strong>Resend</strong> — email delivery.</li>
        </ul>
        <p>We do not sell your data. We do not run third-party ad networks.</p>
      </section>

      <section>
        <h2>Your rights (UK GDPR)</h2>
        <ul>
          <li>You can request a copy of your data at any time.</li>
          <li>You can ask us to correct or delete your data.</li>
          <li>You can turn off the weekly digest from your dashboard.</li>
          <li>
            You can delete your account, which removes your properties, tenant
            details, and uploaded documents from our systems within 30 days.
          </li>
        </ul>
      </section>

      <section>
        <h2>Data retention</h2>
        <p>
          We keep your data for as long as your account is active. If you delete
          your account, we remove your personal data and documents within 30
          days. Anonymised usage logs may be retained longer for security and
          analytics.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          Privacy questions? Email{' '}
          <a href="mailto:privacy@rentalpa.app">privacy@rentalpa.app</a>.
        </p>
      </section>
    </LegalPage>
  );
}
